'use client';

import { bezier } from '@leva-ui/plugin-bezier';
import { folder, useControls } from 'leva';
import { useMemo } from 'react';
import type { CameraRig } from './cameraRig';
import type { BezierPoints, CameraState } from './skyStore';

const points = (value: ArrayLike<number>): BezierPoints => [
  value[0],
  value[1],
  value[2],
  value[3],
];

export const useCameraControls = (rig: CameraRig): CameraState => {
  const [values] = useControls(
    `camera · ${rig.id}`,
    () => {
      const d = rig.defaults;
      return {
        framing: folder(
          {
            pitchTop: {
              value: d.pitchTop,
              label: rig.scroll ? 'pitchTop' : 'pitch',
              min: -60,
              max: 90,
              step: 1,
            },
            fovTop: {
              value: d.fovTop,
              label: rig.scroll ? 'fovTop' : 'fov',
              min: 20,
              max: 110,
              step: 1,
            },
            heightTop: {
              value: d.heightTop,
              label: rig.scroll ? 'heightTop' : 'height',
              min: 0,
              max: 5000,
              step: 1,
            },
            pitchBottom: {
              value: d.pitchBottom,
              min: -60,
              max: 90,
              step: 1,
              render: () => rig.scroll,
            },
            fovBottom: {
              value: d.fovBottom,
              min: 20,
              max: 110,
              step: 1,
              render: () => rig.scroll,
            },
            heightBottom: {
              value: d.heightBottom,
              min: 0,
              max: 5000,
              step: 1,
              render: () => rig.scroll,
            },
          },
          { collapsed: true }
        ),
        easing: folder(
          {
            pitchCurve: {
              ...bezier({ handles: [...d.pitchBezier], graph: true }),
              render: () => rig.scroll,
            },
            heightCurve: {
              ...bezier({ handles: [...d.heightBezier], graph: true }),
              render: () => rig.scroll,
            },
            fovCurve: {
              ...bezier({ handles: [...d.fovBezier], graph: true }),
              render: () => rig.scroll,
            },
            pitchFollow: { value: d.pitchFollow, min: 1, max: 14, step: 0.5 },
            followSpeed: { value: d.followSpeed, min: 1, max: 14, step: 0.5 },
          },
          { collapsed: true }
        ),
        'sway & scroll': folder(
          {
            sway: { value: d.sway, min: 0, max: 3, step: 0.05 },
            swayBottom: {
              value: d.swayBottom,
              min: 0,
              max: 3,
              step: 0.05,
              render: () => rig.scroll,
            },
            darkenOnScroll: {
              value: d.darkenOnScroll,
              min: 0,
              max: 1,
              step: 0.01,
            },
          },
          { collapsed: true }
        ),
      };
    },
    [rig]
  );

  return useMemo(() => {
    const { pitchCurve, heightCurve, fovCurve, ...camera } = values;
    return {
      ...rig.defaults,
      ...camera,
      pitchBottom: rig.scroll
        ? (camera.pitchBottom ?? rig.defaults.pitchBottom)
        : camera.pitchTop,
      heightBottom: rig.scroll
        ? (camera.heightBottom ?? rig.defaults.heightBottom)
        : camera.heightTop,
      fovBottom: rig.scroll
        ? (camera.fovBottom ?? rig.defaults.fovBottom)
        : camera.fovTop,
      swayBottom: rig.scroll
        ? (camera.swayBottom ?? rig.defaults.swayBottom)
        : camera.sway,
      // Leva's bezier values include methods that cannot cross the worker boundary.
      pitchBezier: points(pitchCurve ?? rig.defaults.pitchBezier),
      heightBezier: points(heightCurve ?? rig.defaults.heightBezier),
      fovBezier: points(fovCurve ?? rig.defaults.fovBezier),
    };
  }, [rig, values]);
};
